import { getBackendUrl } from "@Utils";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import useUserCardData from "./useUserCardData";
import type { FormTemplateField, QuestionField } from "@ServerTypes/forms/forms.schema";
import type { UserTagProfileData } from "@ServerTypes/accounts/accounts.schema";

/**
 * The argument object this card hands back to `useForms`' `removeUserFromList`. It is spelled out
 * here rather than imported because the hook still declares the parameter as `any`; the field set
 * below is exactly what this call site sends.
 */
interface RemoveUserFromListArgs {
  fieldIndex: number | null;
  sectionIndex: number | null;
  user: Partial<UserTagProfileData>;
  section: Partial<FormTemplateField>;
  eachField: QuestionField;
  source: string;
  tableRowIndex: number;
  /** Forwarded untouched to the hook, which decides what to do with them. */
  handleCustomFieldData: unknown;
  extraParams: unknown;
}

interface UserCardProps {
  /** The `users`-type question this card belongs to; only `dataToDisplay` is read. */
  field: QuestionField;
  /** Account id of the user shown; the tag store is keyed by it. */
  id: string;
  removeUserFromList?: (args: RemoveUserFromListArgs) => void;
  fieldIndex?: number | null;
  sectionIndex?: number | null;
  section?: Partial<FormTemplateField>;
  source?: string;
  tableRowIndex?: number;
  showAnswersOnly?: boolean;
  handleCustomFieldData?: unknown;
  extraParams?: unknown;
}

function UserCard({
  field,
  id,
  removeUserFromList = () => {},
  fieldIndex = 0,
  sectionIndex = 0,
  section = {},
  source = "",
  tableRowIndex = 0,
  showAnswersOnly,
  handleCustomFieldData,
  extraParams,
}: UserCardProps) {
  const { userData } = useUserCardData({ id });
  // KNOWN BUG, preserved verbatim: dataToDisplay is `.optional()` on the wire but dereferenced
  // unconditionally here, so an absent value throws TypeError. Asserted (erased at emit) to keep the
  // existing runtime; the real fix is a guard plus an empty-state path — a behaviour change, out of
  // scope for a typing pass.
  const dataToDisplay = field.dataToDisplay!;

  return (
    <div className="rounded-md border-solid bg-slate-50 border-2 h-30 p-2 border-button_light flex flex-col gap-2">
      <div className="flex gap-2 items-center justify-around">
        <span>
          <UserNameTagComponent showAvatarOnly id={id} size="LSM" isCircle />
        </span>
        <span>{userData.fullName}</span>
        {!showAnswersOnly && (
          <span
            className="flex items-center"
            onClick={() =>
              removeUserFromList({
                fieldIndex,
                sectionIndex,
                user: userData,
                section,
                eachField: field,
                source,
                tableRowIndex,
                handleCustomFieldData,
                extraParams,
              })
            }
          >
            <IconComponent className="cursor-pointer" name="cross" width="1rem" height="1rem" />
          </span>
        )}
      </div>
      {dataToDisplay.includes("email") && <div>{userData.email}</div>}
      {dataToDisplay.includes("contactNo") && <div>{userData.contactNo}</div>}
      {dataToDisplay.includes("designation") && <div>{userData.designation}</div>}
      {dataToDisplay.includes("org") && <div>{userData.organisationName}</div>}
      {dataToDisplay.includes("location") && <div>{userData.userLocation}</div>}
      {dataToDisplay.includes("userId") && <div>{userData.userId ?? ""}</div>}
      {userData.signature && dataToDisplay.includes("signature") && (
        <div>
          <img className="w-16 h-16" src={`${getBackendUrl()}/fileSystemServer/getSignDocs/${userData.signature}`} />
        </div>
      )}
    </div>
  );
}
export default UserCard;
